import React, { useRef, useState } from 'react';
import './SuccessStoriesCarousel.scss';
import Slider from 'react-slick';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import chevronLeft from '../../../assets/images/chevron-left.png'
import chevronRight from '../../../assets/images/chevron-right.png'
import rightquote from '../../../assets/images/successStoryImages/rightQuote.png';
import leftquote from '../../../assets/images/successStoryImages/leftQuote.png';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

const Arrow = ({ onClick, icon }: any) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      bgcolor: '#fff',
      borderRadius: '50%',
      boxShadow: 1,
      p: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 28,
      width: 28
    }}
  >
    {icon}
  </Box>
);

interface SuccessStoriesCarouselProps {
  data?: any[];
}

const SuccessStoriesCarousel: React.FC<SuccessStoriesCarouselProps> = ({
  data
}) => {
  const sliderRef = useRef<any>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentSlide, setCurrentSlide] = useState(0);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    afterChange: (current: number) => setCurrentSlide(current),
    customPaging: (i: number) => {
    // Use the author or alt from your data for the label
    const testimonial = data?.[i];
    const company = testimonial?.company || '';
    const alt = testimonial?.alt || '';
    // Prefer author, fallback to alt, fallback to number
    const label = company
      ? `Testimonial ${i + 1}, ${company}`
      : alt
        ? `Testimonial ${i + 1}, ${alt}`
        : `Testimonial ${i + 1}`;
    return (
      <button
        type="button"
        aria-label={label}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer'
        }}
      >
        <span style={{ display: 'none' }}>{label}</span>
        <span aria-hidden="true">{i + 1}</span>
      </button>
    );
  },
    responsive: [
      {
        breakpoint: 900, // tablet
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      },
      {
        breakpoint: 600, // mobile
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      }
    ]
  };

  const getFullImageLink = (image: string) => {
  const env = process.env["REACT_APP_ENV_LOADED"];
  let url = `https://content-ndc-portal.aircanada.com`; //default to prod
  if (env === "DEV" || env === "INT" || env === "CRT") {
    url = `https://content-ndc-portal-int.aircanada.com`;
  } else if (env === "BAT" || env === "PREPROD") {
    url = `https://content-ndc-portal-bat.aircanada.com`;
  }
  return url + image;
}

  const totalSlides = data?.length || 0;

  return (
   <Box sx={{ my: 8 }}>
      <Typography
        component="h2"
        id='quotes-heading'
        sx={{
          textAlign: 'start',
          fontWeight: '700 !important',
          // fontFamily: 'Open Sans',
          fontSize: isDesktop
            ? '36px !important'
            : isTablet
              ? '24px !important'
              : '20px !important',
          lineHeight: isDesktop
            ? '48px !important'
            : isTablet
              ? '32px !important'
              : '28px !important',
          mb: 3,
          // ml: {
          //   xs: 'auto',
          //   md: '55px',
          //   lg: '35px'
          // }
        }}
      >
        {t('Testimonials.header')}
      </Typography>

      {/* Apply custom wrapper class to scope the styles */}
      <Box
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)'
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {`Showing testimonial ${currentSlide + 1} of ${totalSlides}`}
      </Box>
      <Box
        className="custom-slick-wrapper"
        sx={{
          width: {
            xs: '100%', // mobile
            sm: '100%', // small tablets
            md: '100%', // tablets
            lg: '100%' // desktop
          },
          // mx: 'auto',
          position: 'relative',
          // boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          boxShadow:
            '0 4px 24px 0 rgba(20, 96, 170, 0.10), 0 1.5px 6px 0 rgba(0,0,0,0.08)',
          borderRadius: '20px',
          backgroundColor: '#FFFFFF !important'
        }}
      >
        {/* Left Arrow */}
        <Box
          role="button"
          tabIndex={0}
          sx={{
            position: 'absolute',
            left: { xs: 0, sm: 0 },
            top: '50%',
            transform: {
              xs: 'translateY(-50%) translateX(-15px)',
              // sm: 'translateY(-50%) translateX(-10px)',
              lg: 'translateY(-50%) translateX(-15px)'
            },
            zIndex: 2
          }}
          onClick={() => sliderRef.current?.slickPrev()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              sliderRef.current?.slickPrev();
            }
          }}
        >
          <span id="prev_testimonial" className="visually-hidden">
            {t('Testimonials.previous', 'Previous testimonial')}
          </span>
          <Arrow
            icon={
              <img
                src={chevronLeft}
                alt="Previous"
                style={{ width:18, height: 18, color: '#000' }}
              />
            }
          />
        </Box>

        {/* Slider */}
        <Slider ref={sliderRef} {...settings}>
          {data?.map((item, index) => (
            <Box
              key={index}
              sx={{
                width: '100%',
                minHeight: isMobile ? '480px !important' : '440px !important',
                textAlign: 'center',
                borderRadius: '20px',
                bgcolor: '#FFFFFF',
                // maxHeight: '500px',
                boxSizing: 'border-box',
                px: { xs: 1, sm: 2, md: 4 }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  minHeight: '250px',
                  mb: isMobile ? 4 : 'auto',
                  px: { xs: 1, sm: 2, md: 4 },
                  pt: isMobile ? 5 : 12
                }}
              >
                <img
                  src={leftquote}
                  alt="left quote"
                  style={{ width: ' 15.73px', height: '11px', marginRight: 8 }}
                />
                <Typography
                  component="p"
                  sx={{
                    // fontFamily: 'Open Sans',
                    fontSize: { xs: '18px', sm: '20px', md: '24px' },
                    lineHeight: { xs: '26px', sm: '28px', md: '32px' },
                    fontWeight: 700,
                    textAlign: 'left',
                    px: 1,
                    display: 'inline'
                  }}
                >
                 {parse(item.quote.value)}
                </Typography>
                <img
                  src={rightquote}
                  alt="right quote"
                  style={{ width: ' 15.73px', height: '11px', marginRight: 24, }}
                />
              </Box>
              <img
                src={getFullImageLink(item["company logo"].url)}
                alt={`logo of ${item["company logo"].alt}`}
                style={{
                  height: '78px',
                  width: '280px',
                  padding: item.id === 1 ? 0 : '25px',
                  objectFit: item.id === 1 ? 'cover' : 'contain',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  // fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontSize: { xs: '14px', sm: '15px', md: '16px' },
                  lineHeight: '20px',
                  mt: 1,
                  mb:5
                }}
              >
                {item.author}
              </Typography>
            </Box>
          ))}
        </Slider>

        {/* Right Arrow */}
        <Box
          role="button"
          tabIndex={0}
          sx={{
            // display: { xs: 'none', lg: 'block', xl: 'block' },
            position: 'absolute',
            right: { xs: 0, sm: 0 },
            // boxshadow: '#e0cfcf 0 0 3px 1px',
            top: '50%',
            transform: {
              xs: 'translateY(-50%) translateX(15px)',
              // sm: 'translateY(-50%) translateX(10px)',
              lg: 'translateY(-50%) translateX(15px)'
            },
            zIndex: 2
          }}
          onClick={() => sliderRef.current?.slickNext()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              sliderRef.current?.slickNext();
            }
          }}
        >
          <Arrow
            role="button"
            icon={
              <>
                <img
                  src={chevronRight}
                  alt="Next"
                  style={{ width: 18, height: 18, color: '#1460AA' }}
                />
                <span id="next_testimonial" className="visually-hidden">
                  {t('Testimonials.next', 'Next testimonial')}
                </span>
              </>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SuccessStoriesCarousel;
